import { useRef, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrency } from 'redux/actions/currencyActions';
import { FiCheck, FiChevronDown } from 'react-icons/fi';

export default function DashCurrencySelector({ size = 'default' }) {
    const dispatch = useDispatch();
    const currencies = useSelector((state) => {
        const d = state.currencyReducer?.currencies;
        return Array.isArray(d) ? d : [];
    });
    const currenciesRates = useSelector((state) => state.currencyReducer.currenciesRates);

    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const options = currencies.filter((c) => c && c.hasRate);

    const currentCode =
        currenciesRates?.symbol?.toUpperCase() ?? currenciesRates?.code?.toUpperCase() ?? 'NGN';

    const getSymbol = (code) => {
        if (code === 'NGN') return '₦';
        if (code === 'USD') return '$';
        if (code === 'GBP') return '£';
        if (code === 'EUR') return '€';
        return code;
    };

    const symbol = getSymbol(currentCode);

    const handleSelect = (currency) => {
        dispatch(selectCurrency(currency));
        setOpen(false);
    };

    const isSmall = size === 'small';
    const height = isSmall ? 32 : 36;
    const px = isSmall ? '0 8px' : '0 10px';
    const fs = isSmall ? 11 : 12;

    if (!options.length) {
        return (
            <div style={{ ...triggerStyles, height, padding: px, fontSize: fs, color: '#94a3b8' }}>
                {symbol} {currentCode}
            </div>
        );
    }

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                onClick={() => setOpen((v) => !v)}
                style={{ ...triggerStyles, height, padding: px, fontSize: fs }}>
                <span style={{ fontSize: isSmall ? 13 : 15, fontWeight: 800, color: '#0A1F3D' }}>
                    {symbol}
                </span>
                <span style={{ color: '#64748b' }}>{currentCode}</span>
                <FiChevronDown
                    size={isSmall ? 12 : 14}
                    style={{
                        color: '#94a3b8',
                        transition: 'transform 0.2s',
                        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                />
            </button>

            {open && (
                <div style={dropdownStyles}>
                    <div style={dropdownHeaderStyles}>Display Currency</div>

                    {options.map((c) => {
                        const code = (c.symbol ?? c.code ?? '').toUpperCase();
                        const sym = getSymbol(code);
                        const isActive = code === currentCode;
                        const isBase = !!c.is_base;
                        const sellRate = Number(c.sell) || 0;
                        const displayRate =
                            isBase || sellRate === 0 || sellRate === 1 ? null : 1 / sellRate;

                        return (
                            <button
                                key={code}
                                onClick={() => handleSelect(c)}
                                style={{
                                    ...rowStyles,
                                    background: isActive ? '#EFF6FF' : 'transparent',
                                }}
                                onMouseEnter={(e) => {
                                    if (!isActive) e.currentTarget.style.background = '#F8FAFC';
                                }}
                                onMouseLeave={(e) => {
                                    if (!isActive) e.currentTarget.style.background = 'transparent';
                                }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <div
                                        style={{
                                            ...iconBoxStyles,
                                            background: isActive ? '#0A1F3D' : '#f1f5f9',
                                        }}>
                                        <span
                                            style={{
                                                fontSize: 11,
                                                fontWeight: 800,
                                                color: isActive ? '#fff' : '#475569',
                                            }}>
                                            {sym.slice(0, 3)}
                                        </span>
                                    </div>
                                    <div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                fontSize: 12,
                                                fontWeight: isActive ? 700 : 600,
                                                color: isActive ? '#0A1F3D' : '#1e293b',
                                            }}>
                                            {code}
                                            {isBase && <span style={basePillStyles}>BASE</span>}
                                        </div>
                                        <div style={{ fontSize: 10, color: '#94a3b8' }}>
                                            {c.name ?? c.currency_name}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                    {isActive ? (
                                        <FiCheck size={14} style={{ color: '#2563EB' }} />
                                    ) : isBase ? (
                                        <span style={{ fontSize: 10, color: '#94a3b8' }}>1.00</span>
                                    ) : displayRate ? (
                                        <>
                                            <div
                                                style={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    color: '#475569',
                                                }}>
                                                {displayRate.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </div>
                                            <div style={{ fontSize: 9, color: '#94a3b8' }}>
                                                per USD
                                            </div>
                                        </>
                                    ) : (
                                        <span style={{ fontSize: 10, color: '#94a3b8' }}>—</span>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

const triggerStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    background: '#f8fafc',
    cursor: 'pointer',
    fontWeight: 600,
    color: '#1e293b',
    whiteSpace: 'nowrap',
    transition: 'border-color 0.15s, background 0.15s',
};
const dropdownStyles = {
    position: 'absolute',
    right: 0,
    top: 'calc(100% + 6px)',
    zIndex: 1000,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
    minWidth: 230,
    maxHeight: 320,
    overflowY: 'auto',
};
const dropdownHeaderStyles = {
    padding: '10px 14px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: 10,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
};
const rowStyles = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background 0.12s',
};
const iconBoxStyles = {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
};
const basePillStyles = {
    fontSize: 9,
    fontWeight: 700,
    background: '#fef9c3',
    color: '#854d0e',
    padding: '2px 5px',
    borderRadius: 4,
};
